import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  AlertTriangle,
  FileText,
  Gavel,
  HeartHandshake,
  Layers,
  Leaf,
  Server,
  ShieldCheck,
  ShieldHalf,
  Sparkles,
  Users
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { DepartmentRetreatEntry } from './DepartmentRetreats';
import Header from '../layout/Header';
import { comitesBZ, comitesSuggeresQC, type ComiteItem } from '../data/comitesData';
import { useAuth } from '../contexts/AuthContext';
import ComiteSanteSecuriteRevisionModal from '../components/ComiteSanteSecuriteRevisionModal';

const iconMap = {
  Gavel,
  Users,
  ShieldCheck,
  FileText,
  Sparkles,
  HeartHandshake,
  AlertTriangle,
  Server,
  Leaf,
  Layers,
  ShieldHalf
};

function ComiteCard({ comite, index, badge, onNavigate, onOpenSst }: { comite: ComiteItem; index: number; badge?: string; onNavigate?: (path: string) => void; onOpenSst?: () => void }) {
  const Icon = iconMap[comite.icone] || Users;

  const handleClick = () => {
    if (comite.id === 'lac-a-epaule' && onNavigate) {
      onNavigate('/comites/lac-a-epaule');
    }
    // Si c'est le CE, rediriger vers la page dédiée
    if (comite.id === 'ce' && onNavigate) {
      onNavigate('/comite-ce');
    }
    // Si c'est le CA, rediriger vers la page dédiée
    if (comite.id === 'ca' && onNavigate) {
      onNavigate('/comite-ca');
    }
    // Si c'est le Comité PRIsME, rediriger vers la page dédiée
    if (comite.id === 'prisme' && onNavigate) {
      onNavigate('/comite-prisme');
    }
    // Si c'est le Comité Sécurité, rediriger vers la page dédiée
    if (comite.id === 'securite' && onNavigate) {
      onNavigate('/comite-securite');
    }
    // Si c'est le Comité RH • Loi 27, rediriger vers la page dédiée
    if (comite.id === 'rh-loi-27' && onNavigate) {
      onNavigate('/comite-rh');
    }
    if (comite.id === 'comite-sante-securite') {
      onOpenSst?.();
    }
  };

  const isNavigableComite = ['lac-a-epaule', 'ce', 'ca', 'prisme', 'securite', 'rh-loi-27', 'comite-sante-securite'].includes(comite.id);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      onClick={handleClick}
      className={`relative overflow-hidden rounded-3xl border border-white/50 shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group h-full ${
        isNavigableComite ? 'cursor-pointer' : ''
      }`}
    >
      {/* Image de fond */}
      <div
        className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
        style={{
          backgroundImage: `url(${comite.imageUrl})`,
        }}
      />
      
      {/* Overlay gradient pour lisibilité */}
      <div className={`absolute inset-0 bg-gradient-to-br ${comite.couleurGradient} opacity-85 group-hover:opacity-90 transition-opacity duration-300`} />
      
      {/* Contenu */}
      <div className="relative p-6 space-y-4 h-full flex flex-col">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-inner text-white">
              <Icon className="w-6 h-6" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-white/80">{comite.niveau}</p>
              <h3 className="text-xl font-semibold text-white drop-shadow">{comite.nom}</h3>
            </div>
          </div>
          {badge && (
            <span className="inline-flex items-center rounded-full bg-white/90 backdrop-blur-sm px-3 py-1 text-xs font-semibold text-gray-800 shadow-inner">
              {badge}
            </span>
          )}
        </div>

        <p className="text-sm text-white/95 leading-relaxed drop-shadow flex-grow">{comite.description}</p>

        <div className="flex flex-wrap items-center gap-2 text-xs font-semibold">
          <span className="rounded-full bg-white/90 backdrop-blur-sm text-gray-800 px-3 py-1 shadow-sm">
            Fréquence: {comite.frequence}
          </span>
          <span className="rounded-full bg-white/90 backdrop-blur-sm text-gray-800 px-3 py-1 shadow-sm">
            Owner: {comite.owner}
          </span>
        </div>

        <div className="space-y-1">
          {comite.objectifs.map((objectif, idx) => (
            <div key={idx} className="flex items-start gap-2 text-sm text-white drop-shadow">
              <span className="mt-1 w-1.5 h-1.5 rounded-full bg-white flex-shrink-0" />
              <span>{objectif}</span>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

export default function Comites() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isComiteSstOpen, setIsComiteSstOpen] = useState(false);

  const pierreLucEmail = 'pierre-luc.fortin@bzinc.ca';

  // Liste des emails autorisés à voir le Dany CA/CE
  const emailsAutorisesCE = [
    'dany.bourassa@bzinc.ca',
    'eric.durand@bzinc.ca',
    'francois.delisle@bzinc.ca',
  ];
  
  // Liste des emails autorisés à voir le comité CA
  const emailsAutorisesCA = [
    'dany.bourassa@bzinc.ca',
    'francois.delisle@bzinc.ca',
    'eric.durand@bzinc.ca',
  ];

  // Liste des emails autorisés à voir le comité Sécurité
  const emailsAutorisesSecurite = [
    'dany.bourassa@bzinc.ca',
    'francois.delisle@bzinc.ca',
    'vincent.bourassa@bzinc.ca',
    'stephane.paradis@bzinc.ca',
    'eric.hains@bzinc.ca',
    'eric.durand@bzinc.ca',
  ];
  
  // Liste des emails autorisés à voir le Comité PRIsME
  const emailsAutorisesPRIsME = [
    'dany.bourassa@bzinc.ca',
    'jean-pierre.leclerc@bzinc.ca',
    'eric.hains@bzinc.ca',
    'francis.marquis@bzinc.ca',
    'jean-michel.caouette@bzinc.ca',
    'pierre-luc.fortin@bzinc.ca',
    'stephane.paradis@bzinc.ca',
    'eric.durand@bzinc.ca',
  ];
  
  // Liste des emails autorisés à voir seulement RH • Loi 27 et Comité social
  const emailsAutorisesRHComites = [
    'emilie.quintal@bzinc.ca',
    'melissa.pelletier@bzinc.ca',
    'louis-alexandre.hebert@bzinc.ca'
  ];
  
  const martinEmail = 'martin.carrier@bzinc.ca';

  const estPierreLucFortin = user?.email?.toLowerCase() === pierreLucEmail;

  // Vérifier si l'utilisateur peut voir le CE
  const peutVoirCE = user?.email && emailsAutorisesCE.includes(user.email.toLowerCase());

  // Vérifier si l'utilisateur peut voir le CA
  const peutVoirCA = user?.email && emailsAutorisesCA.includes(user.email.toLowerCase());

  // Vérifier si l'utilisateur peut voir le comité Sécurité
  const peutVoirSecurite = user?.email && emailsAutorisesSecurite.includes(user.email.toLowerCase());

  // Vérifier si l'utilisateur peut voir le Comité PRIsME
  const peutVoirPRIsME = user?.email && emailsAutorisesPRIsME.includes(user.email.toLowerCase());

  // Vérifier si l'utilisateur est Mélissa ou Émilie (accès limité aux comités RH)
  const estMelissaOuEmilie = user?.email && emailsAutorisesRHComites.includes(user.email.toLowerCase());
  const estMartinCarrier = user?.email?.toLowerCase() === martinEmail;

  // Filtrer les comités BZ selon les permissions
  const comitesBZVisibles = comitesBZ.filter(comite => {
    // Pierre-Luc: accès au Comité PRIsME uniquement (aucun autre comité visible)
    if (estPierreLucFortin) {
      return comite.id === 'prisme';
    }

    // Si c'est Mélissa, Émilie ou Louis-A, voir seulement les comités RH/SST et social
    if (estMelissaOuEmilie) {
      return comite.id === 'rh-loi-27' || comite.id === 'comite-sante-securite' || comite.id === 'social';
    }

    // Si c'est Martin Carrier, pour l'instant aucun comité BZ n'est visible
    if (estMartinCarrier) {
      return false;
    }
    
    // Sinon, appliquer les filtres existants
    if (comite.id === 'ce') {
      return peutVoirCE;
    }
    if (comite.id === 'ca') {
      return peutVoirCA;
    }
    if (comite.id === 'securite') {
      return peutVoirSecurite;
    }
    if (comite.id === 'prisme') {
      return peutVoirPRIsME;
    }
    return true;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50">
      <Header
        title="Comités BZ"
        subtitle="Pilotage clair des comités (stratégie, sécurité, RH, culture) dans un espace unique, au même format que nos pages départements."
      />

      <div className="max-w-7xl mx-auto px-8 pb-14 space-y-12">
        <section className="space-y-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="text-lg font-semibold text-gray-900">Comités actifs BZ</p>
              <p className="text-sm text-gray-600">
                Vue consolidée des comités internes. Chaque carte résume le mandat, la fréquence et les objectifs clés.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <DepartmentRetreatEntry>
              <ComiteCard
                comite={{
                  id: 'lac-a-epaule',
                  nom: 'Lac à l’épaule',
                  description: 'Préparation et ateliers départementaux pour bâtir le cadre et les priorités 2027.',
                  frequence: 'Décembre 2026 · dates à planifier',
                  owner: 'Dany et les directeurs',
                  objectifs: ['Préparer 10 à 20 sujets avant le 10 décembre', 'Deux jours par département', 'Valider les priorités et les suivis 2027'],
                  niveau: 'Stratégique',
                  couleurGradient: 'from-cyan-700 to-blue-900',
                  icone: 'Users',
                  imageUrl: comitesBZ.find(c => c.id === 'ce')?.imageUrl || '',
                }}
                index={0}
                badge="Actif"
                onNavigate={navigate}
              />
            </DepartmentRetreatEntry>
            {comitesBZVisibles.map((comite, index) => (
              <ComiteCard
                key={comite.id}
                comite={comite}
                index={index}
                badge="Actif"
                onNavigate={navigate}
                onOpenSst={() => setIsComiteSstOpen(true)}
              />
            ))}
          </div>
        </section>

        <section className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="text-lg font-semibold text-gray-900">Autres comités recommandés (Québec • TI)</p>
              <p className="text-sm text-gray-600">
                Suggestions récurrentes pour une entreprise TI québécoise (conformité, IA, qualité, ESG).
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {comitesSuggeresQC.map((comite, index) => (
              <ComiteCard key={comite.id} comite={comite} index={index} badge="Suggestion" />
            ))}
          </div>
        </section>

        <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="rounded-2xl border border-blue-100 bg-white/80 p-4 shadow-sm">
            <p className="text-sm font-semibold text-gray-800">Règles d’or</p>
            <ul className="mt-2 space-y-1 text-sm text-gray-700">
              <li>• Agenda, décisions et suivi d’actions documentés.</li>
              <li>• Mandat clair (périmètre, fréquence, quorum).</li>
              <li>• Indicateurs partagés : risques, budget, avancement.</li>
            </ul>
          </div>
          <div className="rounded-2xl border border-green-100 bg-white/80 p-4 shadow-sm">
            <p className="text-sm font-semibold text-gray-800">Alignement Québec</p>
            <ul className="mt-2 space-y-1 text-sm text-gray-700">
              <li>• Loi 27 (travail) et Loi 25 (privacy) suivies.</li>
              <li>• Traçabilité des décisions et preuves de conformité.</li>
              <li>• Plans de relève et de continuité testés.</li>
            </ul>
          </div>
          <div className="rounded-2xl border border-purple-100 bg-white/80 p-4 shadow-sm">
            <p className="text-sm font-semibold text-gray-800">Prochaines étapes</p>
            <ul className="mt-2 space-y-1 text-sm text-gray-700">
              <li>• Lier chaque comité à un backlog d’actions.</li>
              <li>• Ajouter les PV et modèles de rencontres.</li>
              <li>• Publier la charte et le calendrier annuel.</li>
            </ul>
          </div>
        </section>
      </div>
      <ComiteSanteSecuriteRevisionModal
        isOpen={isComiteSstOpen}
        onClose={() => setIsComiteSstOpen(false)}
        contextLabel="Comité actif BZ"
      />
    </div>
  );
}
